import type {
  HyperguestHotel,
  HyperguestProductHotel,
} from '@travel-planner/shared/schemas';
import { formatLocation, resolveTitle } from './title';

// The 5-field detail block shown in the hover tooltip across all three
// columns — a faithful port of the legacy mapper's TooltipContent table
// (Name / Country / City / Street Address / Location).
export type RowDetails = {
  name: string;
  country: string;
  city: string;
  streetAddress: string;
  location: string;
};

const DASH = '—';

const round5 = (n: number): string => Number(n).toFixed(5);

// `locationPoint` is either `{ lat, lng }` or the PostGIS geometry tuple form.
// Drizzle's `geometry({ type: 'point' })` adapter returns the point as an
// `[x, y]` tuple — i.e. `[lng, lat]`, matching the `ST_MakePoint(lng, lat)`
// storage order — so the tuple is lng-first, NOT lat-first.
const formatLocationPoint = (
  point: HyperguestHotel['locationPoint']
): string | null => {
  if (!point) return null;
  if (Array.isArray(point)) {
    const [lng, lat] = point;
    return `${round5(lat)}, ${round5(lng)}`;
  }
  return `${round5(point.lat)}, ${round5(point.lng)}`;
};

// Pull street address + coordinates out of the product's first leg start
// location when present, mirroring legacy `hotel.legs[0].details.startLocation`.
const readProductStartLocation = (
  legs: unknown
): { streetAddress?: string; coordinates?: { lat: number; lng: number } } => {
  if (!Array.isArray(legs) || legs.length === 0) return {};
  const start = (
    legs[0] as { details?: { startLocation?: Record<string, unknown> } }
  )?.details?.startLocation;
  if (!start || typeof start !== 'object') return {};
  const streetAddress =
    (typeof start.streetAddress === 'string' && start.streetAddress) ||
    (typeof start.formattedAddress === 'string' && start.formattedAddress) ||
    undefined;
  const coords = start.coordinates as
    | { latitude?: number; longitude?: number }
    | undefined;
  const coordinates =
    coords &&
    typeof coords.latitude === 'number' &&
    typeof coords.longitude === 'number'
      ? { lat: coords.latitude, lng: coords.longitude }
      : undefined;
  return { streetAddress, coordinates };
};

export const productRowDetails = (row: HyperguestProductHotel): RowDetails => {
  const { streetAddress, coordinates } = readProductStartLocation(row.legs);
  const location =
    (coordinates && `${round5(coordinates.lat)}, ${round5(coordinates.lng)}`) ||
    formatLocationPoint(row.locationPoint) ||
    DASH;
  return {
    name: resolveTitle(row.title),
    country: row.country ?? DASH,
    city: row.city ?? DASH,
    streetAddress: streetAddress ?? DASH,
    location,
  };
};

export const hotelRowDetails = (row: HyperguestHotel): RowDetails => ({
  name: row.title,
  country: row.country ?? DASH,
  city: row.city ?? DASH,
  streetAddress: row.streetAddress ?? DASH,
  location: formatLocationPoint(row.locationPoint) ?? DASH,
});

export { formatLocation };
